/**
 * Starts playback, tolerating both the promise inconsistency and the autoplay policy.
 *
 * `play()` returns a promise on modern browsers and `undefined` on older engines and
 * jsdom, so calling `.catch` on the result directly throws — `Promise.resolve`
 * normalises both. A rejection is swallowed: a blocked autoplay is the browser
 * working as intended, not an error to surface.
 *
 * `unmute` asks for sound, and only gets it when the browser will give it without a
 * fight. The naive version of this — start muted, then clear `muted` once playback
 * is running — looks like it should work and does not: Chrome treats unmuting an
 * element it allowed to autoplay as a new, unpermitted request and PAUSES it. That
 * turned a working muted preview into a still frame, which is worse than silence.
 *
 * So the attempt is made unmuted first. If the browser permits it, there is sound
 * from the start. If it refuses (`NotAllowedError`, the usual case before the viewer
 * has clicked anything), it retries muted so there is at least motion, and the
 * controls on screen make unmuting one click. Sound arrives when the page has earned
 * the right to make noise, rather than by a trick that costs playback.
 */
export function safePlay(
  element: HTMLMediaElement | null | undefined,
  options: { unmute?: boolean } = {},
): void {
  if (!element) return;

  if (!options.unmute) {
    void Promise.resolve(element.play()).catch(() => {});
    return;
  }

  element.muted = false;
  void Promise.resolve(element.play()).catch(() => {
    /* Refused with sound. Fall back to muted playback on the SAME element.
 
       Both guards matter, and each closes a real hole. `isConnected` because the
       promise can settle after a remount (`key={video.id}` swaps the element on every
       entry change), and starting the outgoing video is how two clips end up audible
       at once. `paused` because the caller may have started it again in the meantime
       -- retrying then would restart playback the viewer is already watching. */
    if (!element.isConnected || !element.paused) return;
    element.muted = true;
    void Promise.resolve(element.play()).catch(() => {});
  });
}
